import type { ApiEnvelope, ApiErrorBody, ApiMeta } from './types'

const csrfKey = 'inboxia.csrf'
export const accountStorageKey = 'inboxia.account'
export const authenticationRequiredEvent = 'inboxia:authentication-required'
export const passwordChangeRequiredEvent = 'inboxia:password-change-required'

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly retryable: boolean
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(userFacingErrorMessage(status, body))
    this.name = 'ApiError'
    this.code = body.error.code
    this.status = status
    this.retryable = body.error.retryable
    this.requestId = body.meta?.requestId
  }
}

const apiErrorMessages: Record<string, string> = {
  ACCESS_DENIED: '当前账号无权执行此操作。',
  AUTH_CURRENT_PASSWORD_INVALID: '当前密码不正确，请重新输入。',
  AUTH_INVALID_CREDENTIALS: '账号或密码不正确，请检查后重试。',
  AUTH_MFA_INVALID: '验证码错误或验证流程已过期，请重新验证。',
  AUTH_MFA_UNAVAILABLE: '二次验证暂时不可用，请联系管理员检查认证配置。',
  AUTH_PASSWORD_CHANGE_REQUIRED: '当前密码必须先修改，完成后才能继续。',
  AUTH_RATE_LIMITED: '尝试次数过多，请稍后再试。',
  AUTH_REQUIRED: '登录状态已失效，请重新登录。',
  AUTH_SESSION_INVALID: '登录会话已过期或失效，请重新登录。',
  AUTH_STEP_UP_REQUIRED: '此操作需要重新输入二次验证码。',
  CONFIRMATION_REQUIRED: '请先确认操作影响，再继续。',
  CONTENT_TYPE_INVALID: '请求格式不受支持，请刷新页面后重试。',
  CSRF_INVALID: '页面安全令牌已失效，请重新登录后重试。',
  DRAFTS_FOLDER_REQUIRED: '邮箱缺少可用的草稿箱，请检查 Dovecot 标准邮箱配置后重试。',
  IDEMPOTENCY_KEY_CONFLICT: '该操作已用不同内容提交，请刷新数据后重试。',
  IDEMPOTENCY_KEY_INVALID: '请求标识无效，请重新发起操作。',
  IDEMPOTENCY_PAYLOAD_MISMATCH: '同一请求标识不能用于不同内容，请重新发起操作。',
  HTTP_ERROR: '操作未完成，服务暂时不可用，请稍后重试；如持续出现，请联系管理员。',
  INTERNAL_ERROR: '操作未完成，服务暂时不可用，请稍后重试；如持续出现，请联系管理员。',
  JSON_INVALID: '请求内容无法识别，请刷新页面后重试。',
  MAILBOX_UNAVAILABLE: '当前账号尚未开通有效邮箱；如需收发邮件，请联系管理员创建或恢复邮箱。',
  METHOD_NOT_ALLOWED: '当前操作方式不受支持，请刷新页面后重试。',
  MFA_NOT_ENROLLED: '当前账号尚未绑定二次验证器。',
  MFA_REQUIRED_BY_POLICY: '此操作需要先完成二次验证。',
  PASSWORD_CHANGE_REQUIRED: '当前密码必须先修改，完成后才能继续。',
  PASSWORD_COMPROMISED: '该密码已出现在已知泄露数据中，请更换为全新密码。',
  PASSWORD_POLICY_VIOLATION: '新密码不符合安全要求，请按页面规则修改。',
  PASSWORD_RESET_INVALID: '重置链接无效或已过期，请重新申请。',
  PASSWORD_REUSED: '新密码不能与近期使用过的密码相同。',
  PRECONDITION_REQUIRED: '数据版本信息缺失，请刷新后重试。',
  REQUEST_CANCELED: '操作已取消。',
  REQUEST_TOO_LARGE: '提交的内容过大，请减小后重试。',
  ROUTE_NOT_FOUND: '请求的功能不存在或已变更，请刷新页面。',
  SENT_FOLDER_REQUIRED: '邮箱缺少可用的已发送邮箱，请检查 Dovecot 标准邮箱配置后重试。',
  STEP_UP_REQUIRED: '此操作需要重新输入二次验证码。',
}

function userFacingErrorMessage(status: number, body: ApiErrorBody): string {
  const exact = apiErrorMessages[body.error.code]
  if (exact) return exact
  const code = body.error.code
  if (code.endsWith('_NOT_FOUND')) return '未找到相应数据，它可能已被删除或不在当前权限范围内。'
  if (code.endsWith('_STALE') || code.endsWith('_MISMATCH') || code.endsWith('_CONFLICT')) return '数据已发生变化，请刷新后重试。'
  if (code.endsWith('_TOO_LARGE') || code.endsWith('_LIMIT_EXCEEDED') || code.endsWith('_TOO_MANY_ROWS')) return '提交的内容超过允许范围，请减少后重试。'
  if (code.endsWith('_INVALID')) return '输入内容无效，请检查后重试。'
  if (code.endsWith('_REQUIRED')) return '当前操作缺少必需信息，请补充后重试。'
  if (code.endsWith('_UNAVAILABLE') || code.endsWith('_FAILED')) return '相关服务暂时不可用，请稍后重试。'
  if (status === 400 || status === 422) return '请求内容有误，请检查输入后重试。'
  if (status === 401) return '登录状态已失效，请重新登录。'
  if (status === 403) return '当前账号无权执行此操作。'
  if (status === 404) return '未找到请求的内容。'
  if (status === 409 || status === 412) return '数据已发生变化，请刷新后重试。'
  if (status === 413) return '提交的内容过大，请减少后重试。'
  if (status === 429) return '操作过于频繁，请稍后再试。'
  if (status >= 500) return '操作未完成，服务暂时不可用，请稍后重试。'
  return '操作未完成，请刷新页面后重试。'
}

export function setCSRFToken(token?: string): void {
  sessionStorage.removeItem(csrfKey)
  if (token) {
    // The session cookie is shared by all tabs, so its CSRF token must be too.
    localStorage.setItem(csrfKey, token)
  } else {
    localStorage.removeItem(csrfKey)
  }
}

export function hasCSRFToken(): boolean {
  return Boolean(csrfToken())
}

export function clearStoredAuthentication(): void {
  localStorage.removeItem(csrfKey)
  sessionStorage.removeItem(csrfKey)
  sessionStorage.removeItem(accountStorageKey)
}

function csrfToken(): string | null {
  const shared = localStorage.getItem(csrfKey)
  if (shared) return shared
  const legacy = sessionStorage.getItem(csrfKey)
  if (!legacy) return null
  localStorage.setItem(csrfKey, legacy)
  sessionStorage.removeItem(csrfKey)
  return legacy
}

function handleAuthenticationState(response: Response, body: ApiErrorBody, requestCSRF: string | null): void {
  // An older response must not change authentication established by a newer session.
  if (csrfToken() !== requestCSRF) return
  const sessionInvalid = response.status === 401 && ['AUTH_REQUIRED', 'AUTH_SESSION_INVALID', 'HTTP_ERROR'].includes(body.error.code)
  if (sessionInvalid || body.error.code === 'CSRF_INVALID') {
    clearStoredAuthentication()
    window.dispatchEvent(new Event(authenticationRequiredEvent))
    return
  }
  if (body.error.code === 'PASSWORD_CHANGE_REQUIRED' || body.error.code === 'AUTH_PASSWORD_CHANGE_REQUIRED') {
    try {
      const account = JSON.parse(sessionStorage.getItem(accountStorageKey) ?? 'null') as Record<string, unknown> | null
      if (account && typeof account === 'object') sessionStorage.setItem(accountStorageKey, JSON.stringify({ ...account, mustChangePassword: true }))
    } catch {
      // The route event remains authoritative even if stale browser data is malformed.
    }
    window.dispatchEvent(new Event(passwordChangeRequiredEvent))
  }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const method = (init.method ?? 'GET').toUpperCase()
  const requestCSRF = csrfToken()
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    if (requestCSRF) headers.set('X-CSRF-Token', requestCSRF)
  }
  headers.set('Accept', 'application/json')

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
  })
  const body = (await response.json().catch(() => null)) as ApiEnvelope<T> | ApiErrorBody | null
  if (!response.ok) {
    const errorBody: ApiErrorBody = body && 'error' in body
      ? body
      : { error: { code: 'HTTP_ERROR', message: `HTTP ${response.status}`, retryable: response.status >= 500 } }
    handleAuthenticationState(response, errorBody, requestCSRF)
    throw new ApiError(response.status, errorBody)
  }
  if (response.status === 204) return undefined as T
  if (!body || !('data' in body)) throw new Error('API response did not contain data')
  return body.data
}

export interface ApiPage<T> {
  data: T[]
  page: { nextCursor?: string; hasMore: boolean; sourceTruncated?: boolean }
  meta: ApiMeta & { uidValidity?: number; measuredAt?: string }
}

export async function apiPageRequest<T>(path: string): Promise<ApiPage<T>> {
  const requestCSRF = csrfToken()
  const response = await fetch(path, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
  const body = (await response.json().catch(() => null)) as ApiPage<T> | ApiErrorBody | null
  if (!response.ok) {
    const errorBody: ApiErrorBody = body && 'error' in body
      ? body
      : { error: { code: 'HTTP_ERROR', message: `HTTP ${response.status}`, retryable: response.status >= 500 } }
    handleAuthenticationState(response, errorBody, requestCSRF)
    throw new ApiError(response.status, errorBody)
  }
  if (!body || !('data' in body) || !('page' in body)) throw new Error('API response did not contain page data')
  const data = (body as { data: unknown }).data
  if (data !== null && !Array.isArray(data)) throw new Error('API page data was not an array')
  return { ...body, data: (data ?? []) as T[] }
}

export async function apiBlobRequest(path: string): Promise<Blob> {
  const requestCSRF = csrfToken()
  const response = await fetch(path, { headers: { Accept: 'application/octet-stream' }, credentials: 'same-origin' })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null
    const errorBody: ApiErrorBody = body && 'error' in body
      ? body
      : { error: { code: 'HTTP_ERROR', message: `HTTP ${response.status}`, retryable: response.status >= 500 } }
    handleAuthenticationState(response, errorBody, requestCSRF)
    throw new ApiError(response.status, errorBody)
  }
  return response.blob()
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value)
}
